/**
 * 商品资产管理 —— **一个商品一条档案**（标题 / 卖点 / 语言 / SKU / 平台 / 图组）。
 *
 * 用户口径（2026-10-08）：「新增一个商品资产管理的模块页面，这个是可以让客户
 * 新增，批量上传文件新增的，包括：标题，卖点，语言，图片，需要等等，因为这个可以
 * 让商品详情页可以直接引用，就不需要自己输出产品图片跟文案那些」。
 *
 * ### 为什么它不是一个「素材库的页签」
 *
 * 素材库（顶栏那颗按钮）是**一张图一条**的散图池，回答「我手上有哪些图」；
 * 这里是**一个商品一条**，回答「这个商品叫什么、卖点是什么、用哪几张图」。
 * 详情页要的是后者：它一次要「标题 + 卖点 + 一组产品图」，而从散图池里挑图，
 * 挑完还得回去自己填标题与卖点 —— 那正是这套东西要省掉的活。粒度不同，
 * 所以是两张表、两条接口、两个页面（不 UNION、不合并，见 db.py 建表那段注释）。
 *
 * ### 三条交互口径
 *
 * 1. **筛选与分页都走服务端**（`q` / `language` / `platform` + `page` / `perPage`）。
 *    库里可能上万条，只在本地过滤等于「翻到哪页筛哪页」，而一次性拉满是
 *    「打开就等两秒」——2026-10-09 用户点名要求「做好筛选跟分页」。
 * 2. **筛选条的选项来自 `facets` 接口的真实分布**，不是写死的枚举 ——
 *    写死「德语 / 拉美」这类一条都没用过的档，点下去就是空列表。
 *    （**表单里**的语言下拉是另一回事，那是可选项枚举，见 config/productAssets.ts。）
 * 3. **删除是两段式**（点一次变「再点一次删除」），不用 `window.confirm`——
 *    原生 confirm 会阻塞整页脚本，自动化验收里点不动它，而这条确认又必须可被验证。
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { FILL_FORM_FIELDS, UNCATEGORIZED_CATEGORY } from '@/api/types'
import type {
  CreateProductAssetRequest,
  ProductAsset,
  ProductAssetFacets,
  ProductAssetImage,
  ProductCategory,
} from '@/api/types'
import { AiFillButton } from '@/components/AiFillButton'
import { BatchImageDrop } from '@/components/BatchImageDrop'
import { ImageLightbox } from '@/components/ImageLightbox'
import { Pager } from '@/components/PagedList'
import { RefUploader } from '@/components/RefUploader'
import { SmartImage } from '@/components/SmartImage'
import { Icon } from '@/components/icons'
import {
  Button,
  Card,
  CardBody,
  CardHead,
  Drawer,
  Empty,
  Field,
  GroupedSelect,
  Input,
  Select,
  Textarea,
} from '@/components/ui'
import { PLATFORM_GROUPS } from '@/config/platforms'
import { buildProductFillBrief } from '@/config/prompts'
import {
  DEFAULT_PRODUCT_LANGUAGE,
  PRODUCT_IMAGES_MAX,
  PRODUCT_LANGUAGES,
  PRODUCT_PAGE_SIZE,
  parseProductAssetCsv,
  productAssetCsvSample,
  productAssetCsvTemplate,
  productLanguageLabel,
  type ParsedProductAssetRow,
} from '@/config/productAssets'
import { useToast } from '@/context/ToastContext'
import { rememberFallback } from '@/lib/assetFallback'
import { saveText } from '@/lib/download'

/** 平台下拉的分组选项。**从真源 `PLATFORMS` 派生**，页面里不写死平台清单 */
const PLATFORM_OPTIONS = PLATFORM_GROUPS.map((g, i) => ({
  id: `pg-${i}`,
  title: g.label,
  options: g.list.map((p) => ({ value: p.id, label: p.name })),
}))

export function ProductAssets() {
  const toast = useToast()

  const [items, setItems] = useState<ProductAsset[]>([])
  const [total, setTotal] = useState(0)
  const [facets, setFacets] = useState<ProductAssetFacets | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)

  const [rawQuery, setRawQuery] = useState('')
  const [query, setQuery] = useState('')
  const [language, setLanguage] = useState('')
  const [platform, setPlatform] = useState('')
  /**
   * 分类筛选。三种取值（与 `ProductAssetQuery.category` 同一套口径）：
   * `''` = 全部；`UNCATEGORIZED_CATEGORY` = 未分类；其它 = 分类名。
   */
  const [category, setCategory] = useState('')

  /*
   * 分类清单（**管理抽屉与编辑表单共用这一份**）。
   *
   * ⚠️ 它刻意与 `facets.categories` 分开存：`facets` 是「筛选条要的数字」，
   * 而这个抽屉要做的是增 / 改 / 删 —— 把写操作绑在筛选统计上，
   * 就会变成「管理分类之前必须先成功拉到 facets」，而 facets 失败时
   * 列表是照常显示的（见 `loadFacets`），于是抽屉空着、用户以为没有分类。
   */
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [catsOpen, setCatsOpen] = useState(false)

  /*
   * 分页状态（服务端分页，见文件头约束 1）。`total` 由后端下发 —— 它是
   * **筛选之后**的总数，不能拿 `facets.total`（那是全库总数）去算页数。
   */
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(PRODUCT_PAGE_SIZE)

  const [zoom, setZoom] = useState<{ asset: ProductAsset; index: number } | null>(null)
  const [editor, setEditor] = useState<{ open: boolean; asset: ProductAsset | null }>({
    open: false,
    asset: null,
  })
  const [importOpen, setImportOpen] = useState(false)
  /** 哪一个正在等第二次点击（两段式删除），见文件头约束 3 */
  const [confirming, setConfirming] = useState('')
  const [busy, setBusy] = useState('')

  /* ------------------------------ 取数 ------------------------------ */

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.listProductAssets({
        q: query || undefined,
        language: language || undefined,
        platform: platform || undefined,
        category: category || undefined,
        page,
        perPage,
      })
      // 登记「图床地址 → 本地副本」兜底：图床图被删/被墙时 SmartImage 自动换本地副本
      res.items.forEach((a) => rememberFallback(a.coverUrl, a.coverLocal))
      /*
       * 越界回第 1 页。两种情形都会走到这里：筛选把结果缩到 1 页而用户停在第 5 页；
       * 以及**在这一页把最后几条删完了**。不回退的话界面是一片空白，
       * 而用户刚做完一个动作，只会以为「删坏了」。
       */
      if (!res.items.length && page > 1) {
        setPage(1)
        return
      }
      setItems(res.items)
      setTotal(res.total)
      setFailed(null)
    } catch (e) {
      setFailed(e instanceof Error ? e.message : '商品库加载失败')
    } finally {
      setLoading(false)
    }
  }, [query, language, platform, category, page, perPage])

  /** 筛选条的真实分布。**它失败不该挡住列表** —— 列表才是这一页的主体 */
  const loadFacets = useCallback(async () => {
    try {
      setFacets(await api.productAssetFacets())
    } catch {
      setFacets(null)
    }
  }, [])

  /**
   * 分类清单。同样「失败不挡列表」，但**失败时保留上一次的清单**而不是清空 ——
   * 清空会让正在编辑商品的用户突然发现分类下拉空了，而他什么都没做错。
   */
  const loadCategories = useCallback(async () => {
    try {
      setCategories((await api.listProductCategories()).items)
    } catch {
      /* 保留上一次的清单，理由见上 */
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void loadFacets()
  }, [loadFacets])

  useEffect(() => {
    void loadCategories()
  }, [loadCategories])

  // 搜索防抖：每敲一个字就打一次接口会闪，也会把后端按在地上摩擦
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(rawQuery.trim()), 300)
    return () => window.clearTimeout(t)
  }, [rawQuery])

  /*
   * 任一筛选条件（或每页条数）变化 → 回第 1 页。
   * ⚠️ 不能省：第 5 页 + 筛出 2 条 = 空列表，而 `load` 里那条越界兜底是
   * 「请求回来后发现空了才回第 1 页」—— 中间那一下空白已经闪给用户看了。
   */
  useEffect(() => {
    setPage(1)
  }, [query, language, platform, category, perPage])

  /* ------------------------------ 交互 ------------------------------ */

  const filtered = !!(query || language || platform || category)

  const resetFilters = useCallback(() => {
    setRawQuery('')
    setLanguage('')
    setPlatform('')
    setCategory('')
  }, [])

  /**
   * 关掉分类管理抽屉之后**两边都重取**：facets 管筛选条上的数字，
   * categories 管抽屉自己与编辑表单的下拉。
   *
   * ⚠️ 少了任何一边都会出现「同一个分类在两个地方说法不同」：
   * 只刷 facets → 抽屉里还留着已经删掉的分类；只刷 categories →
   * 筛选条上那个分类的数字是旧的（改名后按新名字点进去还能筛到货，
   * 但数字对不上，用户会去数）。
   */
  const afterCategoriesChanged = useCallback(async () => {
    await Promise.all([loadFacets(), loadCategories(), load()])
  }, [loadFacets, loadCategories, load])

  const openNew = useCallback(() => setEditor({ open: true, asset: null }), [])
  const openEdit = useCallback((asset: ProductAsset) => setEditor({ open: true, asset }), [])
  const closeEditor = useCallback(() => setEditor({ open: false, asset: null }), [])

  const remove = useCallback(
    async (asset: ProductAsset) => {
      if (confirming !== asset.id) {
        // 第一下只是把按钮变成「再点一次删除」—— 不用 window.confirm（会阻塞整页脚本）
        setConfirming(asset.id)
        return
      }
      setConfirming('')
      setBusy(asset.id)
      try {
        await api.deleteProductAsset(asset.id)
        /*
         * 删完**重新取数**（而不是本地 filter 掉一条）。
         * 本地删会让「当前页剩几张」与「total 除以 perPage 的页数」各说各话 ——
         * 差一条的后果是最后一页永远打不开或点出一片空白。服务端才是这份列表的真源。
         */
        await load()
        void loadFacets()
        toast.success('已删除这个商品')
      } catch (e) {
        toast.error(e instanceof Error ? e.message : '删除失败')
      } finally {
        setBusy('')
      }
    },
    [confirming, toast, load, loadFacets],
  )

  /**
   * 保存后重新取数（而不是本地拼一条）—— 服务端还派生了封面，别自己算第二份。
   *
   * ⚠️ **回第 1 页**：列表按 `created_at DESC` 排（新的在前），新增的那条只可能
   * 出现在第 1 页。不回的话，用户在第 3 页保存完会看不到自己刚存的东西，
   * 第一反应是「没保存上」。
   *
   * ⚠️ 还要刷分类清单：在表单里填一个**新**分类名时，后端会顺手把这个分类建出来
   * （`db.ensure_product_category`），不刷的话筛选条上就没有它，用户会以为没建成。
   */
  const afterSave = useCallback(async () => {
    closeEditor()
    setPage(1)
    await load()
    void loadFacets()
    void loadCategories()
  }, [closeEditor, load, loadFacets, loadCategories])

  const afterImport = useCallback(async () => {
    setPage(1)
    await load()
    void loadFacets()
    void loadCategories()
  }, [load, loadFacets, loadCategories])

  /* ------------------------------ 放大 ------------------------------ */

  const zoomItems = useMemo(
    () =>
      (zoom?.asset.images ?? [])
        .filter((i) => i.url)
        .map((i) => ({ url: i.url, thumbUrl: i.thumbUrl || undefined, type: 'image' as const })),
    [zoom],
  )

  /* ------------------------------ 渲染 ------------------------------ */

  const langs = facets?.languages ?? []
  const plats = facets?.platforms ?? []
  const cats = facets?.categories ?? []
  const uncategorized = facets?.uncategorized ?? 0
  /*
   * 分类这一行什么时候渲染：**有分类，或有未分类的商品**。
   *
   * 与语言/平台那两行（`length > 1` 才出）不同：分类**允许只有一个**，
   * 而且「只有一个分类」时那一行恰恰是最有用的（点一下就把不在这类里的排除掉）。
   * 而「一个分类都没有、也全都归好类了」时铺一排空 chip 没有意义。
   */
  const showCategoryRow = cats.length > 0 || uncategorized > 0
  const nothingYet = !loading && !filtered && !items.length
  const pageCount = Math.max(1, Math.ceil(total / perPage))
  const from = total ? (page - 1) * perPage + 1 : 0
  const to = Math.min(page * perPage, total)

  return (
    <div className="page page--wide">
      <Card>
        <CardHead
          title="商品资产"
          hint="一个商品一条档案：标题 · 卖点 · 语言 · 图片。“主图 · 详情”页可直接引用，不必再自己输出产品图与文案"
          icon="box"
          right={
            <>
              <Input
                value={rawQuery}
                onChange={(e) => setRawQuery(e.target.value)}
                placeholder="搜索标题 / SKU / 卖点"
                aria-label="搜索商品"
                data-product-search
                style={{ width: 200 }}
              />
              <Button variant="outline" size="sm" icon="upload" onClick={() => setImportOpen(true)}>
                批量导入
              </Button>
              <Button variant="primary" size="sm" icon="plus" onClick={openNew} data-product-new>
                新增商品
              </Button>
            </>
          }
        />
        <CardBody>
          {failed ? (
            <Empty
              icon="alert"
              title="商品库加载失败"
              text={failed}
              action={
                <Button variant="outline" size="sm" icon="refresh" onClick={() => void load()}>
                  重试
                </Button>
              }
            />
          ) : nothingYet ? (
            <Empty
              icon="box"
              title="还没有商品资产"
              text="录一个商品（标题 + 卖点 + 产品图），之后在「主图 · 详情」页就能直接引用，不用再自己填标题与卖点。"
              action={
                <div className="row" style={{ gap: 8 }}>
                  <Button variant="primary" size="sm" icon="plus" onClick={openNew}>
                    新增第一个商品
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    icon="upload"
                    onClick={() => setImportOpen(true)}
                  >
                    从表格批量导入
                  </Button>
                </div>
              }
            />
          ) : (
            <>
              {/* 筛选 chips —— 选项与计数都来自接口的真实分布（见文件头约束 2）。
                  一条都没有的维度整块不渲染，而不是铺一排点了必空的 chip。 */}

              {/* 分类排在最前：它是用户认货的第一维（先找「我家的那批」，
                  再在同批里按语言/平台细分）。 */}
              {showCategoryRow ? (
                <div className="asset-filter" data-category-row>
                  <span className="asset-filter__label">分类</span>
                  <div className="asset-filter__chips">
                    <button
                      type="button"
                      className="chip"
                      data-active={category === ''}
                      onClick={() => setCategory('')}
                    >
                      全部 {facets?.total ?? 0}
                    </button>
                    {cats.map((c) => (
                      <button
                        key={c.id || c.value}
                        type="button"
                        className="chip"
                        data-active={category === c.value}
                        data-category={c.value}
                        onClick={() => setCategory(category === c.value ? '' : c.value)}
                      >
                        {c.value} {c.count}
                      </button>
                    ))}
                    {/* 「未分类」不是一个分类，所以它不是 `cats` 里的一项 ——
                        它是「category 为空」这个状态的入口。只在真有这类货时出现。 */}
                    {uncategorized > 0 ? (
                      <button
                        type="button"
                        className="chip"
                        data-active={category === UNCATEGORIZED_CATEGORY}
                        data-category-uncategorized
                        onClick={() =>
                          setCategory(
                            category === UNCATEGORIZED_CATEGORY ? '' : UNCATEGORIZED_CATEGORY,
                          )
                        }
                      >
                        未分类 {uncategorized}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {langs.length > 1 ? (
                <div className="asset-filter" data-lang-row>
                  <span className="asset-filter__label">语言</span>
                  <div className="asset-filter__chips">
                    <button
                      type="button"
                      className="chip"
                      data-active={language === ''}
                      onClick={() => setLanguage('')}
                    >
                      全部 {facets?.total ?? 0}
                    </button>
                    {langs.map((l) => (
                      <button
                        key={l.value}
                        type="button"
                        className="chip"
                        data-active={language === l.value}
                        data-lang={l.value}
                        onClick={() => setLanguage(language === l.value ? '' : l.value)}
                      >
                        {productLanguageLabel(l.value)} {l.count}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {plats.length > 1 ? (
                <div className="asset-filter" data-platform-row>
                  <span className="asset-filter__label">平台</span>
                  <div className="asset-filter__chips">
                    {plats.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        className="chip"
                        data-active={platform === p.value}
                        data-platform={p.value}
                        onClick={() => setPlatform(platform === p.value ? '' : p.value)}
                      >
                        {p.value} {p.count}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="asset-toolbar">
                <span className="text-xs muted" data-product-count>
                  {loading
                    ? '加载中…'
                    : filtered
                      ? `筛出 ${total} 个商品`
                      : `共 ${total} 个商品`}
                  {facets ? ` · 全库 ${facets.images} 张图` : ''}
                </span>
                <div className="row" style={{ gap: 6 }}>
                  {/* 分类的**唯一**写入口。放在工具栏而不是分类 chip 行末尾：
                      一个分类都没有时那一行不渲染，入口跟着消失，用户就会
                      找不到「怎么建第一个分类」。 */}
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="folder"
                    onClick={() => setCatsOpen(true)}
                    data-manage-categories
                  >
                    管理分类{cats.length ? `（${cats.length}）` : ''}
                  </Button>
                  {filtered ? (
                    <Button variant="ghost" size="sm" icon="x" onClick={resetFilters}>
                      重置筛选
                    </Button>
                  ) : null}
                </div>
              </div>

              {!items.length ? (
                <Empty
                  icon="search"
                  title="没有匹配的商品"
                  text="换个关键词，或点上面的「重置筛选」。"
                  action={
                    <Button variant="outline" size="sm" icon="x" onClick={resetFilters}>
                      重置筛选
                    </Button>
                  }
                />
              ) : (
                <>
                  <div className="pagrid" data-product-grid>
                    {items.map((a) => (
                      <ProductCard
                        key={a.id}
                        asset={a}
                        busy={busy === a.id}
                        confirming={confirming === a.id}
                        onZoom={(i) => setZoom({ asset: a, index: i })}
                        onEdit={() => openEdit(a)}
                        onRemove={() => void remove(a)}
                      />
                    ))}
                  </div>

                  <Pager
                    page={page}
                    pageCount={pageCount}
                    total={total}
                    from={from}
                    to={to}
                    pageSize={perPage}
                    onPage={setPage}
                    onPageSize={setPerPage}
                    pageSizes={[12, 24, 48, 96]}
                    unit="个"
                  />
                </>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/*
        指路条：这一页的产出是「可被引用的档案」，不是图。
        用户录完商品后下一个动作一定是去出详情页 —— 不指路的话他会回到侧栏
        一格格找（而侧栏口径是「能出哪一类素材」，他从那里认不出该点哪一条）。
      */}
      <div className="batch-hint" style={{ marginTop: 14 }}>
        <Icon name="grid" size={14} />
        <span className="grow">
          录好的商品会被<b>「主图 · 详情」</b>页直接引用：选中一个商品，标题、卖点与产品图
          一次带进去。
        </span>
        <Link className="btn btn--outline btn--sm" to="/detail-page">
          去主图 · 详情
        </Link>
      </div>

      <ProductAssetEditor
        open={editor.open}
        asset={editor.asset}
        categories={categories}
        onClose={closeEditor}
        onSaved={() => void afterSave()}
      />

      <ProductAssetImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => void afterImport()}
      />

      {/* 分类的**唯一**写入口。改名与删除都会动到商品，所以这两个动作
          都必须把「影响了几件货」说出来，见 CategoryManager 里那段说明。 */}
      <CategoryManager
        open={catsOpen}
        categories={categories}
        counts={cats}
        uncategorized={uncategorized}
        onClose={() => setCatsOpen(false)}
        onChanged={() => void afterCategoriesChanged()}
      />

      {zoom && zoomItems.length ? (
        <ImageLightbox
          title={`${zoom.asset.title} · ${zoomItems.length} 张`}
          items={zoomItems}
          index={Math.min(zoom.index, zoomItems.length - 1)}
          onIndex={(i) => setZoom({ asset: zoom.asset, index: i })}
          onClose={() => setZoom(null)}
        />
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* 一张商品卡                                                                  */
/* -------------------------------------------------------------------------- */

function ProductCard({
  asset,
  busy,
  confirming,
  onZoom,
  onEdit,
  onRemove,
}: {
  asset: ProductAsset
  busy: boolean
  confirming: boolean
  onZoom: (index: number) => void
  onEdit: () => void
  onRemove: () => void
}) {
  const shot = asset.coverUrl || asset.images[0]?.url || ''
  const points = asset.sellingPoints.split('\n').filter(Boolean).join(' · ')

  return (
    <article className="pacard" data-product-card data-product-id={asset.id} data-product-sku={asset.sku}>
      {/* 每一格是一个 cell（不是裸按钮）：放大键要挂在封面的**旁边**，
          点图选中、放大走它 —— 与 modelcard / RefUploader 同一套规矩。 */}
      <div className="pacard__cell">
        <button
          type="button"
          className="pacard__shot"
          title="放大看图组"
          aria-label="放大看图组"
          onClick={() => onZoom(0)}
        >
          {shot ? (
            <SmartImage src={shot} alt={asset.title} />
          ) : (
            <span className="pacard__broken">
              <Icon name="alert" size={16} />
            </span>
          )}
          {asset.images.length > 1 ? (
            <span className="pacard__count">{asset.images.length} 张</span>
          ) : null}
        </button>
        {asset.images.length > 1 ? (
          <button
            type="button"
            className="pacard__zoom"
            title="放大看图组"
            aria-label="放大看图组"
            onClick={() => onZoom(0)}
          >
            <Icon name="expand" size={13} />
          </button>
        ) : null}
      </div>

      <div className="pacard__body">
        <div className="pacard__name" title={asset.title}>
          {asset.title}
        </div>
        <div className="pacard__tags">
          {/* 分类排最前：它是用户认货的第一维。空分类**不显示空标签**，
              而是明写「未分类」—— 一块什么都不显示的空白，
              用户会以为这个位置本来就该是空的，而不是「这条还没归类」。 */}
          <span className="pacard__tag" data-product-category={asset.category}>
            {asset.category || '未分类'}
          </span>
          <span className="pacard__tag">{productLanguageLabel(asset.language) || '未设语言'}</span>
          {asset.sku ? <span className="pacard__tag">{asset.sku}</span> : null}
          {/* 批量导入进来的标一下出处：出问题时第一个要问的是「这批是怎么进来的」 */}
          {asset.source === 'import' ? <span className="pacard__tag">表格导入</span> : null}
        </div>
        <div className="pacard__points" title={asset.sellingPoints}>
          {points || '还没有卖点'}
        </div>
        <div className="pacard__src">
          {[asset.platform || '未指定平台', `${asset.images.length} 张图`].join(' · ')}
        </div>
      </div>

      <div className="pacard__acts">
        <Button size="sm" variant="ghost" icon="wand" onClick={onEdit} data-product-edit={asset.id}>
          编辑
        </Button>
        <Button
          size="sm"
          variant={confirming ? 'danger' : 'ghost'}
          icon="trash"
          disabled={busy}
          onClick={onRemove}
          data-product-remove={asset.id}
        >
          {confirming ? '再点一次删除' : '删除'}
        </Button>
      </div>
    </article>
  )
}

/* -------------------------------------------------------------------------- */
/* 分类管理                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 商品分类的**唯一写入口**（新增 / 改名 / 删除）。
 *
 * ### 三条口径
 *
 * 1. **改名与删除都会动到商品**，所以两个动作都必须把「影响了几件货」当面说出来。
 *    用户改一个分类名时，他心里的预期多半是「换个叫法」，而实际发生的是
 *    「名下 30 件货的 `category` 全被改写了」。不说这一句，他就没有依据判断
 *    「我是不是改错了」—— 而改错的代价是他得再改一次，还可能已经忘了原来叫什么。
 *    删除同理：**商品不会跟着消失**，只是变成未分类，这句必须写在确认里。
 *
 * 2. **删除用两段式，不用 `window.confirm`** —— 与商品卡那条同口径
 *    （原生 confirm 会阻塞整页脚本，自动化验收里点不动它，而这条确认必须可被验证）。
 *
 * 3. **分类名是「值」不是「id」**：这里改名就是改名 + 一条批量 UPDATE，
 *    没有「先建一个新的、再把商品搬过去」那套。理由见后端 `db.SCHEMA`。
 *
 * ⚠️ 抽屉里的数字来自 `counts`（`facets.categories`），而清单来自 `categories`
 * （分类接口）。两者**刻意分开**：facets 是「统计」，分类接口是「清单」，
 * 合成一个请求的话，其中一边失败就会让另一边也拿不到 ——
 * 而「删不掉一个分类」和「看不到分类名」是两个完全不同的故障。
 */
function CategoryManager({
  open,
  categories,
  counts,
  uncategorized,
  onClose,
  onChanged,
}: {
  open: boolean
  categories: ProductCategory[]
  counts: { value: string; count: number; id: string }[]
  uncategorized: number
  onClose: () => void
  onChanged: () => void
}) {
  const toast = useToast()
  const [draft, setDraft] = useState('')
  /** 正在改名的那一条（分类 id）+ 输入中的名字 */
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null)
  /** 等第二次点击删除的分类 id */
  const [confirming, setConfirming] = useState('')
  const [busy, setBusy] = useState('')

  // 每次打开都回到干净状态：上次留下的半截改名会让用户以为这条分类已经改过了
  useEffect(() => {
    if (!open) return
    setDraft('')
    setEditing(null)
    setConfirming('')
  }, [open])

  const countOf = useCallback(
    (name: string) => counts.find((c) => c.value === name)?.count ?? 0,
    [counts],
  )

  const add = useCallback(async () => {
    const name = draft.trim()
    if (!name) {
      toast.error('先写一个分类名 —— 它是筛选条上唯一能认出这个分类的东西')
      return
    }
    setBusy('__new__')
    try {
      await api.createProductCategory({ name })
      setDraft('')
      toast.success(`已新建分类「${name}」`)
      onChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '新建分类失败')
    } finally {
      setBusy('')
    }
  }, [draft, toast, onChanged])

  const rename = useCallback(
    async (cat: ProductCategory) => {
      const next = (editing?.name ?? '').trim()
      if (!next || next === cat.name) {
        setEditing(null)
        return
      }
      setBusy(cat.id)
      try {
        const res = await api.updateProductCategory(cat.id, { name: next })
        setEditing(null)
        /*
         * 回执里的 `affected` 是**必须说的那一句**：改名不只改了标签，
         * 名下所有商品的 `category` 一起被改写了。用 toast 而不是静默刷新 ——
         * 用户看不到任何变化时，会怀疑是不是没生效。
         */
        toast.success(
          res.affected > 0
            ? `分类已改名为「${res.category.name}」，名下 ${res.affected} 个商品跟着改了分类`
            : `分类已改名为「${res.category.name}」（名下没有商品）`,
        )
        onChanged()
      } catch (e) {
        // 撞名（409）等错误**保留编辑态**：直接退出的话用户刚敲的名字就没了
        toast.error(e instanceof Error ? e.message : '改名失败')
      } finally {
        setBusy('')
      }
    },
    [editing, toast, onChanged],
  )

  const remove = useCallback(
    async (cat: ProductCategory) => {
      if (confirming !== cat.id) {
        setConfirming(cat.id)
        return
      }
      setConfirming('')
      setBusy(cat.id)
      try {
        const res = await api.deleteProductCategory(cat.id)
        toast.success(
          res.affected > 0
            ? `已删除分类「${cat.name}」，名下 ${res.affected} 个商品变成未分类（商品本身没删）`
            : `已删除空分类「${cat.name}」`,
        )
        onChanged()
      } catch (e) {
        toast.error(e instanceof Error ? e.message : '删除分类失败')
      } finally {
        setBusy('')
      }
    },
    [confirming, toast, onChanged],
  )

  return (
    <Drawer open={open} title="管理分类" onClose={onClose} width={520}>
      <p className="text-xs muted" style={{ marginTop: 0 }}>
        分类只影响「怎么归类」：改名会连带把名下商品的分类一起改掉，删除会把它们变成
        <b>未分类</b>。两种操作都<b>不会删商品</b>。
      </p>

      <div className="row" style={{ gap: 8, marginBottom: 14 }}>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add()
          }}
          placeholder="新建一个分类，如：家居日用"
          aria-label="新分类名"
          data-category-name-input
          style={{ flex: 1 }}
        />
        <Button
          variant="primary"
          size="sm"
          icon="plus"
          disabled={busy === '__new__'}
          onClick={() => void add()}
          data-category-create
        >
          新建
        </Button>
      </div>

      {!categories.length ? (
        <Empty
          icon="folder"
          title="还没有分类"
          text="上面写一个名字就能建 —— 分类可以先建着，之后再往里面放商品。"
        />
      ) : (
        <div className="catlist" data-category-list>
          {categories.map((cat) => {
            const n = countOf(cat.name)
            const isEditing = editing?.id === cat.id
            return (
              <div className="catlist__row" key={cat.id} data-category-item={cat.name}>
                {isEditing ? (
                  <>
                    <Input
                      value={editing.name}
                      autoFocus
                      onChange={(e) => setEditing({ id: cat.id, name: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void rename(cat)
                        if (e.key === 'Escape') setEditing(null)
                      }}
                      aria-label="分类名"
                      data-category-rename-input
                      style={{ flex: 1 }}
                    />
                    <Button
                      size="sm"
                      variant="primary"
                      icon="check"
                      disabled={busy === cat.id}
                      onClick={() => void rename(cat)}
                      data-category-rename-save
                    >
                      保存
                    </Button>
                    <Button size="sm" variant="ghost" icon="x" onClick={() => setEditing(null)}>
                      取消
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="grow">
                      <b>{cat.name}</b>
                      <span className="text-xs muted"> · {n} 个商品</span>
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="edit"
                      onClick={() => setEditing({ id: cat.id, name: cat.name })}
                      data-category-rename={cat.name}
                    >
                      改名
                    </Button>
                    <Button
                      size="sm"
                      variant={confirming === cat.id ? 'danger' : 'ghost'}
                      icon="trash"
                      disabled={busy === cat.id}
                      onClick={() => void remove(cat)}
                      data-category-delete={cat.name}
                    >
                      {confirming === cat.id
                        ? n > 0
                          ? `再点一次删除（${n} 个商品会变未分类）`
                          : '再点一次删除'
                        : '删除'}
                    </Button>
                  </>
                )}
              </div>
            )
          })}
          {uncategorized > 0 ? (
            <div className="catlist__row catlist__row--muted" data-category-item="">
              <span className="grow">
                <b>未分类</b>
                <span className="text-xs muted"> · {uncategorized} 个商品</span>
              </span>
              {/* 「未分类」不是一个分类，所以它没有改名/删除 —— 这两个动作
                  对它都没有意义（改名成什么？删了它这些货去哪？）。
                  在这里**明写一行**而不是不显示：用户在筛选条上见过它，
                  抽屉里找不到会以为漏了。 */}
              <span className="text-xs muted">在商品上选一个分类即可归入</span>
            </div>
          ) : null}
        </div>
      )}
    </Drawer>
  )
}

/* -------------------------------------------------------------------------- */
/* 新增 / 编辑抽屉                                                             */
/* -------------------------------------------------------------------------- */

/**
 * AI 填充能写回的字段。**逐字等于 `FILL_FORM_FIELDS.product`**（那份是白名单
 * 真源，这里只是给它一个 TS 类型，好让 `AiFillButton` 的 `onFill` 拿到键名检查）。
 *
 * ⚠️ 只有这两个：`language` / `platform` 是**选择器**（枚举 id），`sku` 是用户
 * 自己的货号，`category` 是**用户自己定的分类** —— 四类都不归 AI 管，
 * 理由见 `FILL_FORM_FIELDS.product` 的注释。
 */
type ProductAiFields = { title: string; sellingPoints: string }

/**
 * 分类下拉里那个「＋ 新建分类…」选项的 value。
 *
 * ⚠️ 用 `__new__` 这种带下划线的形状，是为了**不可能与真实分类名撞上**：
 * 用户真建一个叫「＋ 新建分类…」的分类是荒谬的，但真建一个叫「新建分类」的
 * 完全可能 —— 那时候哨兵值若是个中文短语，两者就会撞，
 * 症状是「选这个分类却弹出输入框」。
 */
const NEW_CATEGORY = '__new__'

/**
 * 分类 Field 的下拉选项。**清单来自接口**（`categories`），页面里不写死 ——
 * 「清单类枚举不许在页面里硬编码」这条铁律在这里同样适用：
 * 写死一份「家居日用 / 数码配件 / …」，用户自己建的分类就永远选不到。
 *
 * 三项固定内容：
 *   · `''` —— 未分类（是合法状态，不是「没填」）；
 *   · 接口返回的分类，按用户排的序；
 *   · `NEW_CATEGORY` —— 新建入口。
 */
function categoryOptions(categories: ProductCategory[]) {
  return [
    { value: '', label: '未分类' },
    ...categories.map((c) => ({ value: c.name, label: c.name })),
    { value: NEW_CATEGORY, label: '＋ 新建分类…' },
  ]
}

function ProductAssetEditor({
  open,
  asset,
  categories,
  onClose,
  onSaved,
}: {
  open: boolean
  asset: ProductAsset | null
  /** 分类清单。**由页面传进来**（唯一真源 = 分类接口），这里不自己拉一份 ——
   *  抽屉里各拉一次的话，管理分类改完名，编辑表单还是旧清单。 */
  categories: ProductCategory[]
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [sku, setSku] = useState('')
  const [sellingPoints, setSellingPoints] = useState('')
  const [language, setLanguage] = useState(DEFAULT_PRODUCT_LANGUAGE)
  const [platform, setPlatform] = useState('')
  const [category, setCategory] = useState('')
  /** 选了「＋ 新建分类…」之后在输入框里敲的那个名字（见分类那个 Field 的说明） */
  const [newCategory, setNewCategory] = useState('')
  const [note, setNote] = useState('')
  const [refs, setRefs] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  /**
   * 打开时把表单对齐到「当前这条」。**用 `open` 而不是 `asset` 当依赖**：
   * 连续编辑两条时 asset 会变，但用户还没看到抽屉内容那一刻表单就跳了；
   * 而 `open` 从 false → true 才是「这一次编辑开始」的唯一时刻。
   */
  useEffect(() => {
    if (!open) return
    setTitle(asset?.title ?? '')
    setSku(asset?.sku ?? '')
    setSellingPoints(asset?.sellingPoints ?? '')
    setLanguage(asset?.language || DEFAULT_PRODUCT_LANGUAGE)
    setPlatform(asset?.platform ?? '')
    setCategory(asset?.category ?? '')
    setNewCategory('')
    setNote(asset?.note ?? '')
    setRefs((asset?.images ?? []).map((i) => i.url).filter(Boolean))
  }, [open, asset])

  /**
   * 图组：按 url 把**旧记录的元信息接回来**（thumbUrl / localUrl / name）。
   *
   * `RefUploader` 只回一串 url（它是全站唯一的图片入口，不认识「商品图」这个概念），
   * 所以这里做一次对齐。不对齐的话，用户改个标题就会把所有图的本地副本地址抹掉 ——
   * 图床哪天挂了，整个商品库一起变成灰块，而这一下改动看起来与图毫无关系。
   */
  const images = useMemo<ProductAssetImage[]>(() => {
    const old = asset?.images ?? []
    return refs
      .filter(Boolean)
      .slice(0, PRODUCT_IMAGES_MAX)
      .map((url) => {
        const hit = old.find((i) => i.url === url)
        return hit ? { ...hit, url } : { url }
      })
  }, [refs, asset])

  const save = useCallback(async () => {
    if (!title.trim()) {
      toast.error('商品标题不能为空 —— 它是列表里唯一能认出这条记录的东西')
      return
    }
    setSaving(true)
    try {
      const req: CreateProductAssetRequest = {
        title: title.trim(),
        sku: sku.trim(),
        sellingPoints,
        language,
        platform,
        // `__new__` 是下拉里那个「＋ 新建分类…」选项 —— 它代表用户在下面
        // 输入框里敲的那个名字（见分类 Field）。这里把两者合成**一个**值，
        // 所以后端收到的仍然只是「这个商品属于哪个分类」这一个字段。
        category: category === NEW_CATEGORY ? newCategory.trim() : category,
        note: note.trim(),
        images,
      }
      if (asset) {
        await api.updateProductAsset(asset.id, req)
        toast.success('已保存')
      } else {
        await api.createProductAsset(req)
        toast.success('已新增这个商品')
      }
      onSaved()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }, [
    title,
    sku,
    sellingPoints,
    language,
    platform,
    category,
    newCategory,
    note,
    images,
    asset,
    toast,
    onSaved,
  ])

  /**
   * 「AI 帮忙填写」不让点的原因（有值 = 禁用，不隐藏）。
   *
   * 前置条件只有**标题**一条，而且这条是**能力边界**、不是挑剔：本项目的 LLM
   * 通道是纯文本的（`deepseek-flash`，没有视觉能力），brief 里给到模型的
   * 只有文字 —— 标题空着时它手上什么事实都没有，只能编。而编出来的商品名
   * 看着像真的（「XX 系列多功能沥水垫」），用户会一路带到详情页出图才发现
   * 产品是它造的。所以宁可不给点，也不给它自由发挥的余地。
   *
   * 换句话说：用户说的「只有图片跟简单标题」里，**那个简单标题就是入口**。
   */
  const aiFillBlocked = title.trim()
    ? undefined
    : '先在「商品标题」里写几个字（如：硅藻土沥水垫）—— AI 看不了图，只能靠文字补'

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={asset ? `编辑商品 · ${asset.title}` : '新增商品'}
      icon="box"
      width={620}
      footer={
        <>
          <span className="grow text-xs muted">
            {images.length ? `${images.length} 张图（第一张作封面）` : '还没有产品图'}
          </span>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            取消
          </Button>
          <Button
            variant="primary"
            icon="check"
            onClick={() => void save()}
            disabled={saving}
            data-product-save
          >
            {saving ? '保存中…' : asset ? '保存' : '新增'}
          </Button>
        </>
      }
    >
      <Field
        label="商品标题"
        required
        hint="详情页引用时它就是「产品名称」"
        /*
         * 「AI 帮忙填写」放在标题这一行上，而不是卖点那一行 —— 它填的**是标题
         * 本身**（把四个字的短标题撑成能上架的），而标题刚好也是它唯一的
         * 前置条件：按钮变灰的原因就写在它旁边，不用去别处找。
         */
        right={
          <AiFillButton<ProductAiFields>
            kind="product"
            fields={FILL_FORM_FIELDS.product}
            brief={buildProductFillBrief({
              title,
              sku,
              language,
              platform,
              sellingPoints,
              imageCount: images.length,
              /* 只有批量导入的图带原始文件名；直接上传的没有，那就不写这一行 */
              imageNames: images.map((i) => i.name ?? '').filter(Boolean),
              note,
            })}
            sourcePage="product-assets"
            /* 快照必须在**覆盖前**取 —— 组件内取就晚了，那时已经被覆盖 */
            snapshot={{ title, sellingPoints }}
            onFill={(next) => {
              /* 按返回的键逐个写回：模型只补了其中一项时，另一项不该被清空 */
              if (typeof next.title === 'string') setTitle(next.title)
              if (typeof next.sellingPoints === 'string') setSellingPoints(next.sellingPoints)
            }}
            blocked={aiFillBlocked}
            label="AI 帮忙填写"
            busyLabel="AI 正在写…"
          />
        }
      >
        <Input
          value={title}
          placeholder="如：硅藻土沥水垫"
          onChange={(e) => setTitle(e.target.value)}
          data-product-title
        />
      </Field>

      <Field label="SKU / 货号" hint="批量导入时，图片文件名按它自动归位（也可以用标题或行号）">
        <Input value={sku} placeholder="如：HYS-001" onChange={(e) => setSku(e.target.value)} />
      </Field>

      <Field
        label="卖点 / 文案要点"
        hint="一行一个卖点。详情页引用后可直接编辑，不必重新敲一遍"
      >
        <Textarea
          value={sellingPoints}
          rows={4}
          placeholder={'0.5s 速吸\n可折叠收纳\n耐高温 200℃'}
          onChange={(e) => setSellingPoints(e.target.value)}
          data-product-points
        />
      </Field>

      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        <div className="grow">
          <Field label="分类" hint="用来归类与筛选，可先建空分类">
            <Select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value)
                // 从「新建」切回来时清掉半截输入 —— 留着的话下次再点「新建」
                // 会看到上次敲的字，像是被记住了（其实并没有保存过）
                if (e.target.value !== NEW_CATEGORY) setNewCategory('')
              }}
              data-product-category
            >
              {categoryOptions(categories).map((o) => (
                <option key={o.value || '__none__'} value={o.value}>
                  {o.label}
                </option>
              ))}
              {/* 库里那条商品的分类已经不在分类表里（分类被删了、或手工 SQL 改过）
                  时补一个选项 —— 否则下拉会落到第一项「未分类」上，
                  用户只是想改个标题，一保存就把分类抹掉了。与语言那一格同一条规矩。 */}
              {category &&
              category !== NEW_CATEGORY &&
              !categories.some((c) => c.name === category) ? (
                <option value={category}>{category}（已有值）</option>
              ) : null}
            </Select>
          </Field>
          {/* 「＋ 新建分类…」选中的时候就地输入名字。
              这不是「第二个分类管理入口」—— 它只往**这个字段**里写一个值，
              后端保存商品时会顺手把分类建出来（`db.ensure_product_category`）；
              改名和删除仍然只在那一个「管理分类」抽屉里。 */}
          {category === NEW_CATEGORY ? (
            <div style={{ marginTop: 8 }}>
              <Input
                value={newCategory}
                autoFocus
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="新分类名，如：家居日用"
                aria-label="新分类名"
                data-product-new-category
              />
              <p className="text-xs muted" style={{ margin: '6px 0 0' }}>
                保存后会建出这个分类，并在左边的筛选条上出现。
              </p>
            </div>
          ) : null}
        </div>
        <div className="grow">
          <Field label="语言" hint="这个商品文案与图内文字的语言">
            <select
              className="select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              data-product-language
            >
              {/* 库里存了一个不在清单里的语言（表格导入的）时补一个选项 ——
                  否则下拉会显示成第一项，用户一保存就把原值改掉了 */}
              {PRODUCT_LANGUAGES.every((l) => l.id !== language) && language ? (
                <option value={language}>{language}（已有值）</option>
              ) : null}
              {PRODUCT_LANGUAGES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grow">
          <Field label="平台" hint="留空 = 不限定">
            <GroupedSelect
              value={platform}
              onChange={setPlatform}
              groups={[{ id: 'none', title: '不限', options: [{ value: '', label: '不限定' }] }, ...PLATFORM_OPTIONS]}
              ariaLabel="投放平台"
            />
          </Field>
        </div>
      </div>

      <RefUploader
        wrap="none"
        label="产品图"
        fieldHint={`第一张作为封面 · 最多 ${PRODUCT_IMAGES_MAX} 张。详情页引用时整组带过去`}
        urls={refs}
        onChange={setRefs}
        max={PRODUCT_IMAGES_MAX}
        dropLabel="拖拽或点击上传产品图"
        dropHint="支持多选 · 可拖拽排序 · 第一张作为主图"
      />

      <Field label="备注" hint="只给你自己看，不参与生成">
        <Input value={note} placeholder="如：首单 500 件" onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Drawer>
  )
}

/* -------------------------------------------------------------------------- */
/* 批量导入抽屉                                                                */
/* -------------------------------------------------------------------------- */

/**
 * 批量导入 —— **一份 CSV + 一个文件夹的图**。
 *
 * 两步，顺序不能反：
 *   ① 表格进来 → 得到 N 行商品；
 *   ② 图片进来 → 按文件名归位到行（`assignImagesToRows`）。
 * 反过来（先传图后传表）时归位函数拿不到任何行，只能把图全列进「未归位」——
 * 所以界面上第二步在没表时是禁用态，而不是让它白跑一次。
 *
 * ⚠️ 图片归位用的是**已上传成功的地址**，而 `upload()` 的返回顺序与输入顺序
 * 一一对应（见 AssetLibraryContext 的 for...of push）—— 靠 `Assigned.index`
 * 接回「哪一张图对应哪一行」。不靠文件名回查：后端落盘时会把文件名改成 uuid，
 * 响应里的 `fileName` 只是回显，不是契约。
 */
function ProductAssetImportDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean
  onClose: () => void
  onImported: () => void
}) {
  const toast = useToast()

  const [rows, setRows] = useState<ParsedProductAssetRow[]>([])
  const [fatal, setFatal] = useState('')
  const [ignored, setIgnored] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [source, setSource] = useState('')
  const [pasted, setPasted] = useState('')
  /** 行号 → 已归位的图地址（由 BatchImageDrop 分发进来） */
  const [assigned, setAssigned] = useState<Map<number, string[]>>(new Map())
  const [submitting, setSubmitting] = useState(false)

  /**
   * 归位要的行标识。`RowKey.productName` 就是这里的商品标题 ——
   * 归位规则（SKU 全等 / 标题全等 / 纯数字行号 / 含 SKU）复用 `lib/batchImages.ts`，
   * 与批量出图**同一份实现**：两边各写一套，「什么样的文件名算对得上」
   * 就会在两个页面给出不同答案，而用户会以为是自己命名不规范。
   */
  const rowKeys = useMemo(
    () => rows.map((r) => ({ line: r.line, sku: r.sku, productName: r.title })),
    [rows],
  )

  const reset = useCallback(() => {
    setRows([])
    setFatal('')
    setIgnored([])
    setWarnings([])
    setSource('')
    setPasted('')
    setAssigned(new Map())
  }, [])

  // 关闭即清空：下次打开时看到上一次的残留表格，用户会以为「已经导进去了」
  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const applyText = useCallback((text: string, name: string) => {
    const res = parseProductAssetCsv(text)
    setRows(res.rows)
    setFatal(res.fatal)
    setIgnored(res.ignoredHeaders)
    setWarnings(res.warnings)
    setSource(name)
    // 换了表就把归位结果清掉：行号对不上新表，留着会「给第 3 行配了上一张表的图」
    setAssigned(new Map())
  }, [])

  const readFile = useCallback(
    async (file: File) => {
      try {
        applyText(await file.text(), file.name)
      } catch (e) {
        toast.error(e instanceof Error ? e.message : '这个文件读不出来')
      }
    },
    [applyText, toast],
  )

  /**
   * 归位回调：`BatchImageDrop` 送来的「行号 → 这次新增的图地址」。
   *
   * **合并而不是覆盖** —— 用户可以分几批拖（先拖 20 张，再拖文件夹里剩下的）。
   * 覆盖的话第二批会把第一批抹掉，而界面上「已归位 N 张」的数字看着还挺正常。
   */
  const applyAssign = useCallback((byLine: Map<number, string[]>) => {
    setAssigned((prev) => {
      const next = new Map(prev)
      byLine.forEach((list, line) => {
        const merged = next.get(line) ?? []
        list.forEach((u) => {
          if (u && !merged.includes(u)) merged.push(u)
        })
        next.set(line, merged)
      })
      return next
    })
  }, [])

  /* --------------------------- 提交 --------------------------- */

  const badLines = rows.filter((r) => !r.title.trim()).map((r) => r.line)
  const importable = rows.filter((r) => r.title.trim())

  const submit = useCallback(async () => {
    if (!importable.length) {
      toast.error('没有可导入的商品')
      return
    }
    if (badLines.length) {
      // 后端也会整批拒（并点名行号），本地先拦一道是为了把人话给到眼前
      toast.error(`第 ${badLines.join('、')} 行没有标题 —— 补上或删掉这几行再导入`)
      return
    }
    setSubmitting(true)
    try {
      const items: CreateProductAssetRequest[] = importable.map((r) => ({
        title: r.title.trim(),
        sku: r.sku,
        sellingPoints: r.sellingPoints,
        language: r.language,
        platform: r.platform,
        // 分类列写了一个还不存在的名字时，后端会**顺手把这个分类建出来**
        // （`db.ensure_product_category`）—— 所以这里原样透传，不在前端
        // 先建分类：那两个请求之间失败一次，就会留下一个空分类。
        category: r.category,
        // 本地归位的图在前（第一张作封面），CSV 里给的公网地址接在后面
        images: [
          ...(assigned.get(r.line) ?? []).map((url) => ({ url })),
          ...r.imageUrls.map((url) => ({ url })),
        ].slice(0, PRODUCT_IMAGES_MAX),
      }))
      const res = await api.bulkCreateProductAssets({ items })
      toast.success(`已导入 ${res.created} 个商品`)
      onImported()
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '导入失败')
    } finally {
      setSubmitting(false)
    }
  }, [importable, badLines, assigned, toast, onImported, onClose])

  const assignedCount = useMemo(
    () => [...assigned.values()].reduce((n, list) => n + list.length, 0),
    [assigned],
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="批量导入商品"
      icon="upload"
      width={760}
      footer={
        <>
          <span className="grow text-xs muted" data-import-summary>
            {rows.length
              ? `${importable.length} 个商品可导入 · ${assignedCount} 张图已归位`
              : '先导入表格'}
          </span>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            variant="primary"
            icon="check"
            onClick={() => void submit()}
            disabled={submitting || !importable.length || badLines.length > 0}
            data-import-submit
          >
            {submitting ? '导入中…' : `导入 ${importable.length || ''} 个商品`}
          </Button>
        </>
      }
    >
      {/* ---------------------------- 第 1 步：表格 ---------------------------- */}
      <div className="row row--between" style={{ marginBottom: 8 }}>
        <b>① 导入表格</b>
        <div className="row" style={{ gap: 8 }}>
          {/*
            「填一份示例」—— 用户 2026-10-09 问的「导入的案例有了吗」就是这一步。
            光有模板（空表 + 占位行），用户还是不知道「填满了长什么样」：
            卖点几行、多个值怎么分隔、图片文件名与 SKU 怎么对上。
            点一下就把 3 个样例商品铺进下面的粘贴框并立刻解析 ——
            「点导入会发生什么」这一步不用真的猜。
          */}
          <Button
            variant="ghost"
            size="sm"
            icon="wand"
            data-import-sample
            onClick={() => {
              const text = productAssetCsvSample()
              setPasted(text)
              applyText(text, '内置示例')
            }}
          >
            填一份示例
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon="download"
            onClick={() =>
              saveText('商品资产导入模板.csv', productAssetCsvTemplate(), 'text/csv')
            }
          >
            下载模板
          </Button>
        </div>
      </div>

      <p className="text-xs muted" style={{ marginTop: 0 }}>
        一行一个商品。列名写中文或英文都认：标题（必填）、卖点、语言、SKU、平台、图片文件名、图片地址。
        <br />
        不确定格式？点「填一份示例」—— 会铺 3 个样例商品（含多行卖点与图片），可以直接导入看看。
      </p>

      {/*
        CSV 的文件选择器 —— **刻意不用 `ui.Dropzone`**，而是这里一个裸 input。
        两条理由：
          · `Dropzone` 被冒烟测试钉死为「只服务素材库上传」（它没有张数上限、
            不排序、不产生「本次要传给上游的参考图」，与 RefUploader 职责不同，
            所以它自己是一条独立通道 —— 但那意味着它**不该**被别的页面借用）；
          · accept 明确写成 `.csv,.txt` 的输入框本来就被那条断言放行
            （它专门为「批量导入 CSV 台账」留了口子：这不可能是参考图上传）。
        这里也不需要 `Dropzone` 的拖拽高亮与 busy 态 —— 选文件是一次系统弹窗，
        而「拖拽」这条路有更顺的替代：直接把表格内容粘到下面那个框里。
      */}
      <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <label className="btn btn--outline btn--sm" data-import-csv>
          <Icon name="upload" size={13} />
          {source ? `已导入：${source}` : '选择 CSV 文件'}
          <input
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0]
              // 清 value：同一个文件选第二次也要能触发 change，否则用户
              // 「改了文件再选一次」会毫无反应（这是 file input 的经典坑）
              e.target.value = ''
              if (f) void readFile(f)
            }}
          />
        </label>
        {source ? (
          <Button variant="ghost" size="sm" icon="x" onClick={reset}>
            清空
          </Button>
        ) : null}
      </div>

      <Field label="或直接粘贴表格内容" hint="第一行必须是表头">
        <Textarea
          value={pasted}
          rows={4}
          placeholder={'标题,卖点,语言,SKU,平台,图片文件名\n硅藻土沥水垫,0.5s 速吸,zh-CN,HYS-001,amazon,HYS-001_1.jpg'}
          onChange={(e) => setPasted(e.target.value)}
          onBlur={() => {
            if (pasted.trim()) applyText(pasted, '粘贴的表格')
          }}
          data-import-paste
        />
      </Field>

      {fatal ? (
        <div
          className="text-xs"
          style={{ color: 'hsl(var(--destructive))', marginBottom: 10 }}
          data-import-fatal
        >
          {fatal}
        </div>
      ) : null}

      {ignored.length ? (
        <p className="text-xs muted" data-import-ignored>
          认不出这几列，已忽略：{ignored.join('、')}
        </p>
      ) : null}
      {warnings.length ? (
        <p className="text-xs" style={{ color: 'hsl(var(--destructive))' }} data-import-warnings>
          {warnings.join('；')}
        </p>
      ) : null}

      {/* ---------------------------- 第 2 步：图片 ---------------------------- */}
      {rows.length ? (
        /*
         * ② 补图 + ③ 核对，装进**已有的批量图分发器**（`BatchImageDrop`）。
         *
         * 为什么复用它而不是自己再收一遍图：它就是为这件事写的 ——
         * 「一批散文件 → 认得出属于哪一行的，逐行归位」，而且已经处理了三个
         * 手写版一定会漏的点：
         *   · **只传认得出的**（先花掉上传再告诉用户「这几张放不进去」，
         *     等于让他为一批没用的地址买单，还顺带把素材库塞脏）；
         *   · **串行分批**（一次丢 200 张上去，上游网关的限流重试会叠在一起）；
         *   · **认不出的不猜，列出来让用户点一下**（归错行的后果是
         *     「A 的商品配了 B 的图」，而任务照记成功、界面一切正常）。
         *
         * 两个页面共用同一份归位规则与同一套上传节奏，用户在 `/batch` 学会的
         * 操作在这里原样成立 —— 这正是「同一件事只许一份实现」要的效果。
         */
        <BatchImageDrop
          rows={rowKeys}
          onAssign={applyAssign}
          disabledHint="先把表格导进来 —— 图片要按表格里的 SKU / 标题 / 行号归位"
        >
          <div className="row row--between" style={{ margin: '14px 0 8px' }}>
            <b>② 补产品图</b>
            <span className="text-xs muted">
              把图片拖到这里（或下面的表格上）· 文件名含 SKU / 标题 / 行号即自动归位
            </span>
          </div>

          {/* ---------------------------- 预览 ---------------------------- */}
          <div className="row row--between" style={{ margin: '2px 0 8px' }}>
            <b>③ 核对</b>
            <span className="text-xs muted">标题为空的行导不进去，可以先删掉</span>
          </div>

          <div className="patable__wrap">
            <table className="patable" data-import-table>
              <thead>
                <tr>
                  <th>行</th>
                  <th>标题</th>
                  <th>语言</th>
                  <th>SKU</th>
                  <th>图</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.line} data-bad={!r.title.trim()} data-import-row={r.line}>
                    <td>{r.line}</td>
                    <td className="patable__cell" title={r.title}>
                      {r.title || <span style={{ color: 'hsl(var(--destructive))' }}>缺标题</span>}
                    </td>
                    <td>{productLanguageLabel(r.language)}</td>
                    <td>{r.sku || '—'}</td>
                    <td>
                      {(assigned.get(r.line)?.length ?? 0) + r.imageUrls.length}
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="icon"
                        icon="x"
                        aria-label={`删掉第 ${r.line} 行`}
                        onClick={() => setRows((prev) => prev.filter((x) => x.line !== r.line))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </BatchImageDrop>
      ) : null}
    </Drawer>
  )
}
